import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import "../styles/main.css";
import styles from "../styles/licenseList.module.css"; // Reuse license styles
import {
  getTrainingPlans,
  getTrainingPlanPermissions,
  updateTrainingPlanPermission,
  getManufacturersData,
  getComingSoonManufacturers,
  getTrainingsListWithPermissions,
  deleteTrainingPlan
} from "./helpers/requests";
import { formatFirestoreDate, isFirestoreDateExpired } from "./helpers/helpers";
import ConfirmDeleteTrainingPlanModal from "./modals/confirmDeleteTrainingPlanModal";
import ConfirmHideModal from "./modals/confirmHideModal";
import ConfirmPermissionModal from "./modals/confirmPermissionModal";
import CreateTrainingPlanModal from "./modals/createTrainingPlanModal";
import EditTrainingPlanModal from "./modals/editTrainingPlanModal";
import { useAuth } from "../firebase/auth.tsx";
import { useTableFilters } from "./hooks/useTableFilters.tsx";
import TableHeaderFilter from "./common/TableHeaderFilter.tsx";

interface SelectedTraining {
  id?: string;
  name?: string;
  entityId?: string; // targetId (planId)
  permission?: boolean;
}

const TrainingPlanList = ({ plans, setPlans }) => {
  const { t } = useTranslation();
  const { headquarterId: routeHeadquarterId, headsetId: routeHeadsetId } = useParams<{ headquarterId?: string; headsetId?: string }>();
  const { userRole, userId } = useAuth();
  const isHeadsetManager = userRole === "headsetAdmin";
  
  // Removed local plans state
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [trainingsList, setTrainingsList] = useState<any[]>([]);
  const [comingSoonManufacturers, setComingSoonManufacturers] = useState<any[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [collapsedManufacturers, setCollapsedManufacturers] = useState<Set<string>>(new Set());
  
  // Modals
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [showDeletePlanModal, setShowDeletePlanModal] = useState(false);
  const [showConfirmActionModal, setShowConfirmActionModal] = useState(false);
  const [showConfirmPermissionModal, setShowConfirmPermissionModal] = useState<SelectedTraining | false>(false);
  
  const [pendingAction, setPendingAction] = useState<{
    manufacturerId: string;
    manufacturerName: string;
    actionType: "select" | "hide";
    value: boolean;
  } | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter hook
  const {
    filteredData: filteredPlans,
    filterPanels,
    filters,
    searchQueries,
    panelRefs,
    toggleFilterPanel,
    handleFilterChange,
    handleFilterSearch,
    setFilters
  } = useTableFilters(plans || []);

  useEffect(() => {
    fetchData();
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOptionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [routeHeadquarterId, routeHeadsetId]);

  const fetchData = async () => {
    setIsLoadingPlans(true);
    try {
      const filters: any = {};
      if (routeHeadquarterId) filters.headquarterId = routeHeadquarterId;
      if (routeHeadsetId) filters.deviceId = routeHeadsetId;
      
      const data = await getTrainingPlans(filters);
      setPlans(data);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const groupTrainingsByManufacturer = (trainings) => {
    const grouped: any = {};
    trainings.forEach((t: any) => {
        if (!grouped[t.manufacturerId]) {
            grouped[t.manufacturerId] = {
                id: t.manufacturerId,
                name: t.manufacturerName,
                trainings: []
            };
        }
        grouped[t.manufacturerId].trainings.push(t);
    });
    return Object.values(grouped);
  };

  const handlePlanClick = async (plan: any) => {
    setSelectedPlan(plan);
    setIsLoadingPermissions(true);
    
    try {
      // Use training plan as entity type
      const [allTrainings, comingSoon] = await Promise.all([
        getTrainingsListWithPermissions(plan.id, "trainingPlan", false),
        getComingSoonManufacturers(plan.id, "trainingPlan", false)
      ]);

      const enrichedTrainings = groupTrainingsByManufacturer(allTrainings);
      setTrainingsList(enrichedTrainings);
      setComingSoonManufacturers(comingSoon);
      
      const hiddenManufacturers = new Set<string>();
      enrichedTrainings.forEach((m: any) => {
        if (m.trainings.length > 0 && m.trainings.every((t: any) => t.isHidden)) {
           hiddenManufacturers.add(m.id);
        }
      });
      setCollapsedManufacturers(hiddenManufacturers);

    } catch (error) {
      console.error("Error fetching plan permissions:", error);
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  const handlePermissionChange = async (trainingId: string, targetId: string, newPermissionState: boolean) => {
    if (!selectedPlan) return;

    let isHidden = false;
    for (const m of trainingsList) {
        const t = m.trainings.find((tr: any) => tr.id === trainingId);
        if (t) { isHidden = t.isHidden; break; }
    }

    try {
      await updateTrainingPlanPermission(targetId, trainingId, newPermissionState, isHidden);
      
      setTrainingsList(prevList => 
        prevList.map(m => ({
          ...m,
          trainings: m.trainings.map(t => 
            t.id === trainingId ? { ...t, permission: newPermissionState } : t
          )
        }))
      );
    } catch (error) {
      console.error("Error updating plan permission:", error);
    }
  };

  const handleSelectAll = (manufacturerId: string, shouldSelect: boolean) => {
    const manufacturer = trainingsList.find(m => m.id === manufacturerId);
    if (!manufacturer) return;

    setPendingAction({
      manufacturerId,
      manufacturerName: manufacturer.name,
      actionType: "select",
      value: shouldSelect
    });
    setShowConfirmActionModal(true);
  };

  const confirmBulkAction = async () => {
    if (!pendingAction || !selectedPlan) return;

    const { manufacturerId, actionType, value } = pendingAction;
    setShowConfirmActionModal(false);
    setPendingAction(null);

    const manufacturer = trainingsList.find(m => m.id === manufacturerId);
    if (manufacturer) {
        const originalTrainingsList = [...trainingsList];

        setTrainingsList(prevList => 
            prevList.map(m => m.id === manufacturerId ? {
                ...m,
                trainings: m.trainings.map((t: any) => ({
                    ...t,
                    permission: actionType === "select" ? value : t.permission,
                }))
            } : m)
        );

        try {
            await Promise.all(
              manufacturer.trainings.map((t: any) => {
                 const newPermission = actionType === "select" ? value : t.permission;
                 return updateTrainingPlanPermission(selectedPlan.id, t.id, newPermission, t.isHidden);
              })
            );
        } catch (error) {
            console.error("Error in bulk update:", error);
            setTrainingsList(originalTrainingsList);
        }
    }
  };

  const toggleCollapse = (manufacturerId: string) => {
    setCollapsedManufacturers(prev => {
      const next = new Set(prev);
      if (next.has(manufacturerId)) next.delete(manufacturerId);
      else next.add(manufacturerId);
      return next;
    });
  };

  return (
    <div className={styles.adminContainer}>
      <div className={styles.leftContainer}>
        <div className={styles.tableHeader}>
          <table className="table table-borderless">
            <thead>
              <tr>
                <th style={{ width: "20%" }}>{t("Code")}</th>
                <th style={{ width: "30%" }}>
                  <TableHeaderFilter
                    column="userName"
                    title={t("User")}
                    data={plans || []}
                    filterPanels={filterPanels}
                    filters={filters}
                    searchQueries={searchQueries}
                    panelRefs={panelRefs}
                    toggleFilterPanel={toggleFilterPanel}
                    handleFilterChange={handleFilterChange}
                    handleFilterSearch={handleFilterSearch}
                    setFilters={setFilters}
                  />
                </th>
                <th style={{ width: "20%" }}>
                  <TableHeaderFilter
                    column="headsetName"
                    title={t("Headset")}
                    data={plans || []}
                    filterPanels={filterPanels}
                    filters={filters}
                    searchQueries={searchQueries}
                    panelRefs={panelRefs}
                    toggleFilterPanel={toggleFilterPanel}
                    handleFilterChange={handleFilterChange}
                    handleFilterSearch={handleFilterSearch}
                    setFilters={setFilters}
                  />
                </th>
                {!routeHeadquarterId && !routeHeadsetId && !isHeadsetManager && (
                    <th style={{ width: "20%" }}>
                      <TableHeaderFilter
                        column="headquarterName"
                        title={t("Headquarter")}
                        data={plans || []}
                        filterPanels={filterPanels}
                        filters={filters}
                        searchQueries={searchQueries}
                        panelRefs={panelRefs}
                        toggleFilterPanel={toggleFilterPanel}
                        handleFilterChange={handleFilterChange}
                        handleFilterSearch={handleFilterSearch}
                        setFilters={setFilters}
                      />
                    </th>
                )}
                <th style={{ width: "10%" }}></th>
              </tr>
            </thead>
          </table>
        </div>
        <div className={styles.tableContent}>
          {isLoadingPlans ? (
            <div className={styles.loading}>{t("Loading")}...</div>
          ) : (
            <div className={`container-fluid ${styles.containerFluid}`}>
            <table className="table table-borderless">
              <tbody>
                {filteredPlans.map((plan, index) => (
                  <tr 
                    key={plan.id} 
                    style={{ backgroundColor: index % 2 === 0 ? "#D4E5E2" : "transparent" }}
                  >
                    <td style={{ width: "20%" }}>
                      <div className={styles.licenseInfoWrapper}>
                        {plan.code}
                        <div style={{display: "flex", gap: "10px", marginTop: "5px"}}>
                            <span className={`${styles.licenseDate} ${isFirestoreDateExpired(plan.expirationDate) ? styles.expiredDate : ""}`}>{t("Expiration_Date")}: {formatFirestoreDate(plan.expirationDate, t)}</span>
                        </div>
                      </div>
                    </td>
                    <td style={{ width: "30%" }}>{plan.userName}</td>
                    <td style={{ width: "20%" }}>{plan.headsetName}</td>
                    {!routeHeadquarterId && !routeHeadsetId && !isHeadsetManager && (
                        <td style={{ width: "20%" }}>{plan.headquarterName}</td>
                    )}
                    <td style={{ width: "10%" }} className={styles.editButtonWrapper}>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handlePlanClick(plan)}
                            style={{
                              backgroundColor: selectedPlan?.id === plan.id ? "#404040" : "#39A686",
                              borderColor: selectedPlan?.id === plan.id ? "#404040" : "#39A686",
                            }}
                          >
                            {t("View")}
                        </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      <div className={styles.rightContainer}>
        {selectedPlan ? (
          <>
            <div className={styles.containerHeaderWrapper}>
              <div className={styles.containerHeader}>
                <div className={styles.userDataWrapper}>
                  <div className={styles.userDataName}>{selectedPlan.code}</div>
                  <div className={`${styles.userDataEmail} ${isFirestoreDateExpired(selectedPlan.expirationDate) ? styles.expiredDate : ""}`}>{t("Expiration_Date")}: {formatFirestoreDate(selectedPlan.expirationDate, t)}</div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                  <div className="dropdown" ref={dropdownRef}>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setIsOptionsOpen(!isOptionsOpen)}
                        style={{ backgroundColor: "#39A686", borderColor: "#39A686", borderRadius: "10px" }}
                      >
                        &#9013; {t("Options")}
                    </button>
                    {isOptionsOpen && (
                        <div className={styles.dropdownMenu}>
                            <button onClick={() => {setShowEditPlanModal(true); setIsOptionsOpen(false);}}>
                                <img src="/images/Icon_Edit.png" alt="Edit" />
                                {t("Edit_Plan")}
                            </button>
                            <button onClick={() => {setShowDeletePlanModal(true); setIsOptionsOpen(false);}}>
                                <img src="/images/Icon_Delete.png" alt="Delete" />
                                {t("Delete_Plan")}
                            </button>
                        </div>
                    )}
                  </div>
              </div>
              <hr />
              <p className={styles.containerHeaderTitle}>{t("Plan_Permissions")}</p>
              <p className={styles.containerHeaderDescription}>{t("Manage_trainings_for_this_plan")}</p>
            </div>

            <div className={`${styles.trainingsContainer} thin-scrollbar`}>
              {isLoadingPermissions ? (
                <p className={styles.loading}>{t("Loading")}...</p>
              ) : (
                <>
                {trainingsList.map((manufacturer) => {
                    const isManufacturerHidden = manufacturer.trainings.every((t: any) => t.isHidden);
                    const isCollapsed = collapsedManufacturers.has(manufacturer.id);
                    const allSelected = manufacturer.trainings.every((t: any) => t.permission);

                    return (
                  <div key={manufacturer.id} className={styles.trainingsGroup}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {!isManufacturerHidden && (
                      <span 
                        onClick={() => toggleCollapse(manufacturer.id)}
                        style={{ cursor: "pointer", marginRight: "10px", fontSize: "0.8em", userSelect: "none" }}
                      >
                          {isCollapsed ? "\u25B6" : "\u25BC"}
                      </span>
                      )}
                      <label className={styles.trainingsGroupTitle} style={{ color: isManufacturerHidden ? "gray" : "#39a686", marginBottom: 0 }}>
                        {manufacturer.name}
                      </label>
                      
                      {!isCollapsed && !isManufacturerHidden && (
                         <span 
                            onClick={() => handleSelectAll(manufacturer.id, !allSelected)}
                            style={{ marginLeft: "1vh", fontSize: "0.7em", cursor: "pointer", color: "#212529" }}
                         >
                            {allSelected ? t("Unselect_all") : t("Select_all")}
                         </span>
                      )}
                    </div>
                    {!isCollapsed && (
                    <div className={styles.collapsibleWrapper}>
                        <ul className="list-unstyled">
                          {manufacturer.trainings.map((training: any) => (
                            <li key={training.id} className="form-check custom-checkbox">
                                <label className="custom-checkbox-label">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={training.permission}
                                    onChange={() => setShowConfirmPermissionModal({
                                        id: training.id,
                                        name: training.name,
                                        entityId: selectedPlan.id,
                                        permission: training.permission,
                                    })}
                                    id={`training-${training.id}`}
                                    disabled={training.isHidden}
                                />
                                <span className="checkmark"></span>
                                <span className="form-check-label" style={{ color: training.isHidden ? "gray" : "#212529" }}>
                                    {training.name}
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                    </div>
                    )}
                  </div>
                )})}
                </>
              )}
            </div>
          </>
        ) : (
          <p className={styles.selectUser}>{t("Select_plan_to_view_permissions")}</p>
        )}
      </div>

      {/* Modals */}
      {showCreatePlanModal && (
        <CreateTrainingPlanModal
          setShowCreatePlanModal={setShowCreatePlanModal}
          handleCreatePlan={(newPlan) => setPlans(prev => [newPlan, ...prev])}
          headquarterId={routeHeadquarterId}
        />
      )}

      {showEditPlanModal && selectedPlan && (
        <EditTrainingPlanModal
          setShowEditPlanModal={setShowEditPlanModal}
          plan={selectedPlan}
          handleUpdatePlan={(updatedPlan) => {
            setPlans(prev => prev.map(p => p.id === updatedPlan.id ? updatedPlan : p));
            setSelectedPlan(updatedPlan);
          }}
        />
      )}

      {showDeletePlanModal && selectedPlan && (
        <ConfirmDeleteTrainingPlanModal
          setShowConfirmDeleteModal={setShowDeletePlanModal}
          planId={selectedPlan.id}
          handleDelete={async () => {
            setPlans(prev => prev.filter(p => p.id !== selectedPlan.id));
            setSelectedPlan(null);
            setShowDeletePlanModal(false);
          }}
        />
      )}

      {showConfirmActionModal && pendingAction && (
        <ConfirmHideModal
          setShowConfirmHideModal={setShowConfirmActionModal}
          manufacturerId={pendingAction.manufacturerId}
          manufacturerName={pendingAction.manufacturerName}
          handleHideClick={confirmBulkAction}
          title={pendingAction.value ? t("Select_all") : t("Unselect_all")}
          description={pendingAction.value ? t("Confirm_bulk_select") : t("Confirm_bulk_unselect")}
          confirmText={t("Confirm")}
          cancelText={t("Cancel")}
        />
      )}

      {showConfirmPermissionModal && (
        <ConfirmPermissionModal
            setShowConfirmPermissionModal={() => setShowConfirmPermissionModal(false)}
            trainingId={(showConfirmPermissionModal as SelectedTraining).id}
            trainingName={(showConfirmPermissionModal as SelectedTraining).name}
            targetId={(showConfirmPermissionModal as SelectedTraining).entityId}
            targetName={selectedPlan.userName}
            permission={(showConfirmPermissionModal as SelectedTraining).permission}
            handlePermissionChange={handlePermissionChange}
        />
      )}
    </div>
  );
};

export default TrainingPlanList;